/**
 * /login — the unified Customer Portal sign-in (ADR 013: CP is the single IdP).
 *
 * Visually mirrors the AUCNET Stock sign-in (`aucnet-stock-front-user`): the
 * AuthSplitLayout shows a showroom photo + brand lockup on the left and the
 * form card on the right. Sign-in is email (login ID) + password, with the
 * configured OAuth providers below.
 *
 * Passwordless "email me a login link" (magic-link) sign-in is intentionally
 * NOT offered here — email login is disabled portal-wide (see
 * ENABLE_MAGIC_LINK_AUTH, default off). The `/login/verify` route + the
 * server-side `/verify-login-link` endpoint are retained because they are also
 * the signup email-verification landing, but no UI issues a login link.
 *
 * Spec: docs/specs/PORTAL_AUTHZ_AUTHN_SSOT.md, PORTAL_AUTH_OAUTH_SSOT.md,
 *       psm#68 (Login UI), Provide Cars brand SSOT (2026-05-14).
 *
 * - Seeds portal_csrf via a preflight GET /me on mount.
 * - Surfaces a single generic "email or password is incorrect" message so a
 *   failed sign-in does not distinguish unknown-email from wrong-password.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  looksLikeEmail,
  loginWithPassword,
  seedCsrfAndProbeSession,
  PasswordAuthError,
} from '../lib/auth';
import { sanitiseRedirect } from '../lib/redirect';
import { STOCK_HANDOFF_PATH, isStockSsoTarget } from '../lib/stockSession';
import { getAuthFeatureFlags, getStockRegisterUrl } from '../lib/featureFlags';
import { AuthDivider, OAuthButtons } from '../components/auth/OAuthButtons';
import { AuthSplitLayout } from '../components/auth/AuthSplitLayout';
import { PublicShell } from '../components/layout/PublicShell';
import { BRAND } from '../components/layout/designTokens';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string; canRetry: boolean };

const RATE_LIMITED_MESSAGE = 'Too many requests. Please wait a moment and try again.';
const VALIDATION_MESSAGE = 'Please check the format of your email address.';
const INVALID_CREDENTIALS_MESSAGE = 'The email address or password is incorrect.';

const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: BRAND.navy900,
  marginBottom: '0.375rem',
};

const FIELD_INPUT: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '0.625rem 0.75rem',
  fontSize: '0.9375rem',
  color: BRAND.navy900,
  background: BRAND.white,
  border: `1px solid ${BRAND.slate300}`,
  borderRadius: '0.5rem',
  outline: 'none',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
};

const PRIMARY_BUTTON: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: BRAND.gold500,
  color: BRAND.navy950,
  border: 0,
  borderRadius: 9999,
  fontSize: '0.9375rem',
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: '0.01em',
  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
};

const CARD_TITLE: React.CSSProperties = {
  margin: '0 0 1.25rem',
  fontSize: '1.5rem',
  fontWeight: 700,
  color: BRAND.navy900,
  letterSpacing: '-0.01em',
};

const PW_TOGGLE: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: '0.625rem',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 0,
  padding: '0.25rem 0.375rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: BRAND.slate500,
  cursor: 'pointer',
};

export function LoginPage() {
  const [params] = useSearchParams();
  const prefilledEmail = (() => {
    const raw = params.get('email');
    if (!raw) return '';
    const trimmed = raw.trim();
    return looksLikeEmail(trimmed) ? trimmed : '';
  })();
  const flags = getAuthFeatureFlags();
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });
  const [alreadyAuthed, setAlreadyAuthed] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  // Stock's "Sign in" sends ?sso_target=<stock>/sso-callback: after signing in
  // here, hand the session back to Stock (also through OAuth, which carries
  // redirectTo as its returnTo).
  const redirectTo = isStockSsoTarget(params.get('sso_target'))
    ? STOCK_HANDOFF_PATH
    : sanitiseRedirect(params.get('redirect'));
  const formRef = useRef<HTMLFormElement>(null);

  const providerError = params.get('error');

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      const outcome = await seedCsrfAndProbeSession(ctrl.signal);
      if (outcome === 'authenticated') {
        setAlreadyAuthed(true);
        navigate(redirectTo, { replace: true });
      }
    })();
    return () => ctrl.abort();
  }, [navigate, redirectTo]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (state.kind === 'submitting') return;
    const value = email.trim();
    if (!looksLikeEmail(value)) {
      setState({ kind: 'error', message: VALIDATION_MESSAGE, canRetry: true });
      return;
    }
    setState({ kind: 'submitting' });
    try {
      await loginWithPassword({ email: value, password });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof PasswordAuthError && err.code === 'RATE_LIMITED') {
        setState({ kind: 'error', message: RATE_LIMITED_MESSAGE, canRetry: true });
        return;
      }
      setState({ kind: 'error', message: INVALID_CREDENTIALS_MESSAGE, canRetry: true });
    }
  }

  if (alreadyAuthed) {
    return (
      <PublicShell flush>
        <AuthSplitLayout>
          <h1 style={CARD_TITLE}>Sign in to your account</h1>
          <p style={{ margin: 0, fontSize: '0.875rem', color: BRAND.slate500 }}>
            You&apos;re already signed in. Redirecting…
          </p>
        </AuthSplitLayout>
      </PublicShell>
    );
  }

  const providerErrorMessage = (() => {
    if (!providerError) return null;
    switch (providerError) {
      case 'oauth_cancelled':
        return 'Sign-in was cancelled.';
      case 'oauth_verify_failed':
        return "We couldn't complete sign-in. Please try again.";
      case 'account_link_required':
        return 'This email address is already registered with a different sign-in method. Please sign in using your existing method, then link them on the settings page.';
      case 'member_not_registered':
        return 'No member account was found for that sign-in. Please create an account first.';
      default:
        return null;
    }
  })();

  const stockRegisterUrl = providerError === 'member_not_registered' ? getStockRegisterUrl() : null;

  const banner = providerErrorMessage ? (
    <div
      role="alert"
      data-testid="login-provider-error"
      style={{
        color: '#7F1D1D',
        background: '#FEF2F2',
        border: '1px solid #FCA5A5',
        padding: '0.625rem 0.875rem',
        borderRadius: '0.5rem',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
      }}
    >
      {providerErrorMessage}
      {stockRegisterUrl ? (
        <>
          {' '}
          <a
            href={stockRegisterUrl}
            data-testid="login-stock-register-link"
            style={{ color: '#7F1D1D', fontWeight: 600, textDecoration: 'underline' }}
          >
            Create an account on AUCNET Stock
          </a>
        </>
      ) : null}
    </div>
  ) : null;

  return (
    <PublicShell flush>
      <AuthSplitLayout>
        <h1 style={CARD_TITLE}>Sign in to your account</h1>
        {banner ? <div style={{ marginBottom: '1rem' }}>{banner}</div> : null}
        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: '0.875rem' }}>
            <label htmlFor="login-email" style={FIELD_LABEL}>
              Email address
            </label>
            <input
              id="login-email"
              type="email"
              name="email"
              autoComplete="email"
              required
              disabled={state.kind === 'submitting'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={FIELD_INPUT}
              aria-invalid={state.kind === 'error' ? 'true' : 'false'}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = BRAND.gold500;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(201,169,78,0.18)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = BRAND.slate300;
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ marginBottom: '0.875rem' }}>
            <label htmlFor="login-password" style={FIELD_LABEL}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                type={passwordVisible ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                required
                disabled={state.kind === 'submitting'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...FIELD_INPUT, paddingRight: '3.75rem' }}
                aria-invalid={state.kind === 'error' ? 'true' : 'false'}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = BRAND.gold500;
                  e.currentTarget.style.boxShadow = `0 0 0 3px rgba(201,169,78,0.18)`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = BRAND.slate300;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => setPasswordVisible((v) => !v)}
                aria-pressed={passwordVisible}
                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                disabled={state.kind === 'submitting'}
                style={PW_TOGGLE}
              >
                {passwordVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          {state.kind === 'error' ? (
            <div
              role="alert"
              data-testid="login-error"
              style={{
                color: '#7F1D1D',
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                marginBottom: '0.875rem',
              }}
            >
              {state.message}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={state.kind === 'submitting'}
            style={{
              ...PRIMARY_BUTTON,
              cursor: state.kind === 'submitting' ? 'wait' : 'pointer',
              opacity: state.kind === 'submitting' ? 0.7 : 1,
            }}
          >
            {state.kind === 'submitting' ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {flags.googleOAuth || flags.microsoftOAuth ? (
          <>
            <AuthDivider label="or" />
            <OAuthButtons returnTo={redirectTo} intent="login" />
          </>
        ) : null}
        <div
          style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: BRAND.slate700,
          }}
        >
          {/* CP self-registration is disabled — accounts are created in
              AUCNET Stock (AS) and auto-provisioned into CP. No "Create one"
              link here; AS is the sole registration entry. */}
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: BRAND.slate500 }}>
            <Link to="/" style={{ color: BRAND.slate500, textDecoration: 'none' }}>
              ← Back to home
            </Link>
          </p>
        </div>
      </AuthSplitLayout>
    </PublicShell>
  );
}
